/**
 * WizardMobileBar — wspólny mobilny szkielet kreatora (lg:hidden).
 *
 * Jedno źródło prawdy dla mobilnej części "chrome" WSZYSTKICH kreatorów:
 *  - pływający przycisk "Podgląd" (otwiera arkusz z podglądem na żywo),
 *  - przyklejony dolny pasek akcji: Wstecz / Zapisz / Dalej (lub Opublikuj),
 *  - dolny arkusz (bottom sheet) z podglądem strony.
 *
 * Na desktopie (lg+) nawigacja odbywa się przez WizardTopbar + listę kroków po lewej —
 * dlatego ten komponent jest w całości ukryty na lg+.
 */
import { useState } from 'react';
import { Loader2, Check, Save, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';

export const WizardMobileBar = ({
  prefix,
  step,
  totalSteps,
  isLastStep,
  submitting,
  nextDisabled,
  publishLabel = 'Opublikuj',
  onBack,
  onSaveExit,
  onNext,
  previewNode,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <>
      {/* PŁYWAJĄCY PRZYCISK PODGLĄDU */}
      {previewNode && (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          data-testid={`${prefix}-mobile-preview-btn`}
          className="lg:hidden fixed bottom-20 right-5 z-30 flex items-center gap-2 px-4 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-300/50 font-semibold text-sm"
        >
          <Eye className="w-4 h-4" />
          Podgląd
        </button>
      )}

      {/* PRZYKLEJONY DOLNY PASEK AKCJI */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white/95 backdrop-blur-md border-t border-gray-200 px-3 py-2.5 flex items-center gap-2"
        data-testid={`${prefix}-mobile-bottom-bar`}
      >
        <button
          type="button"
          onClick={onBack}
          disabled={step === 1 || submitting}
          data-testid={`${prefix}-mobile-back-btn`}
          className="flex-shrink-0 w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 flex items-center justify-center text-gray-700"
          aria-label="Wstecz"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {onSaveExit && (
          <button
            type="button"
            onClick={onSaveExit}
            disabled={submitting}
            data-testid={`${prefix}-mobile-save-btn`}
            className="flex-shrink-0 h-12 px-3 rounded-xl bg-orange-50 hover:bg-orange-100 disabled:bg-gray-50 disabled:text-gray-300 flex items-center justify-center gap-1.5 text-orange-600 font-medium text-sm"
            aria-label="Zapisz i wróć"
          >
            <Save className="w-4 h-4" />
            <span className="hidden xs:inline">Zapisz</span>
          </button>
        )}

        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          data-testid={`${prefix}-mobile-next-btn`}
          className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold flex items-center justify-center gap-2 shadow-md shadow-orange-200/60"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isLastStep ? (
            <Check className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
          {isLastStep ? publishLabel : 'Dalej'}
        </button>
      </div>

      {/* DOLNY ARKUSZ PODGLĄDU */}
      {previewOpen && previewNode && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid={`${prefix}-mobile-preview-sheet`}>
          <button
            type="button"
            aria-label="Zamknij podgląd"
            onClick={() => setPreviewOpen(false)}
            className="absolute inset-0 bg-black/50 animate-in fade-in duration-200"
          />
          <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-3xl shadow-2xl max-h-[88vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
            <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
              <div className="w-12 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-4 pb-2 border-b border-gray-100 flex-shrink-0">
              <p className="text-sm font-bold text-gray-900">Podgląd Twojej strony</p>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                data-testid={`${prefix}-mobile-preview-close`}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">{previewNode}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default WizardMobileBar;
